import { TurnstileInstance } from '@marsidev/react-turnstile'
import Head from 'next/head'
import { FormEventHandler, useRef, useState } from 'react'

import Submit from '@/components/buttons/Primary'
import Layout from '@/components/Layout'
import TurnstileWidget from '@/components/TurnstileWidget'
import { Status, useStatus } from '@/hooks/useStatus'

export default function ContactUs(): JSX.Element {
	const [name, setName] = useState('')
	const [email, setEmail] = useState('')
	const [phone, setPhone] = useState('')
	const [comments, setComments] = useState('')
	const turnstileRef = useRef<TurnstileInstance>(null)
	const [status, setStatus] = useStatus(Status.Idle)
	const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
	const [widgetVisible, setWidgetVisible] = useState(false)

	const handleContactSubmission: FormEventHandler = event => {
		event.preventDefault()

		if (turnstileToken === null) {
			return
		}

		setStatus(Status.Loading)

		fetch('/api/contact', {
			body: JSON.stringify({
				email,
				name,
				phone: phone === '' ? null : phone,
				comments,
				turnstileToken,
			}),
			headers: {
				'Content-Type': 'application/json',
			},
			method: 'POST',
		}).then(response => {
			if (response.ok) {
				setStatus(Status.Success)
				window.scrollTo(0, 0)

				setName('')
				setEmail('')
				setPhone('')
				setComments('')
				setTurnstileToken(null)
				turnstileRef.current?.reset()
			} else {
				throw new Error('Submission failed')
			}
		}).catch(() => {
			setStatus(Status.Error)
			setTurnstileToken(null)
			turnstileRef.current?.reset()
		})
	}

	return (
		<div className="bg-gray-100">
			<Head>
				<title key="page-title">Contact Us | Pace International</title>
			</Head>

			<div className="container py-8 px-6 mx-auto sm:flex sm:items-start sm:justify-center md:py-12 md:px-8 xl:px-16">
				<div className="bg-white shadow rounded-lg pt-3 pb-4 px-3 md:p-6 lg:py-6 lg:px-9">
					<h3 className="text-center text-xl font-bold sm:text-left md:text-3xl">Contact Us</h3>

					<div className="flex justify-between items-start mt-6">
						<p className="w-1/3 font-semibold sm:hidden">Address:</p>
						<p className="w-2/3 text-sm sm:w-full md:text-lg lg:w-3/4">3582 Technology Dr. NW Rochester, MN 55901</p>
					</div>

					<div className="flex justify-between items-center mt-3">
						<p className="w-1/3 font-semibold sm:hidden">Phone:</p>
						<a className="w-2/3 text-sm text-green-400 font-extrabold sm:w-full md:text-lg" href="tel:18004447223">1-800-444-7223</a>
					</div>
				</div>

				<div>
					{status === Status.Success ? (
						<div className="bg-white shadow rounded-lg pt-3 pb-6 px-3 mt-3 sm:flex sm:justify-between sm:items-center sm:mt-0 sm:mb-4 sm:ml-6 md:p-6 md:mb-6 lg:pt-6 lg:pb-9 lg:mb-8 lg:px-12 xl:mb-9">
							<div className="text-center sm:text-left">
								<h3 className="text-xl font-bold md:text-3xl">Thank you for your request!</h3>
								<p className="mt-2 text-sm md:text-base">One of our experts will be getting in contact with you right away.</p>
							</div>

							<svg className="hidden sm:block sm:ml-8" width="48" height="42" viewBox="0 0 48 42" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M16.3245 34.148L43.5264 1.36361C44.4399 0.247118 46.0639 0.145618 47.0789 1.05912C48.1954 1.97261 48.2969 3.59661 47.3834 4.61161L17.5425 40.644C17.5425 40.644 16.832 41.5575 15.614 41.5575C14.396 41.5575 13.4825 40.644 13.4825 40.644L0.795013 27.043C-0.118485 26.028 -0.321484 24.5056 0.592013 23.4906C1.60701 22.3741 3.33251 22.3741 4.3475 23.3891L14.396 34.0465C14.396 34.0465 14.802 34.554 15.411 34.554C16.02 34.554 16.3245 34.148 16.3245 34.148Z" fill="#8DC63F" />
							</svg>
						</div>
					) : (
						null
					)}

					<div className="bg-white shadow rounded-lg pt-3 pb-6 px-3 mt-3 sm:mt-0 sm:ml-6 md:p-6 lg:pt-6 lg:pb-9 lg:px-12">
						<div className="text-center lg:text-left">
							<h3 className="text-xl font-bold md:text-3xl">We're here to help!</h3>
							<p className="text-sm mt-2.5 md:text-base lg:mt-3">Enter your information below and one of our experts will get in touch with you as soon as possible.</p>
						</div>

						<form className="mt-6 md:mt-9 lg:mt-12" onSubmit={handleContactSubmission}>
							<div>
								<label className="text-sm md:text-base" htmlFor="name">Name</label>
								<input value={name} onChange={event => setName(event.target.value)} required className="h-8 w-full shadow-input appearance-none rounded mt-1.5 px-2 py-1.5 md:h-11" type="text" name="name" id="name" />
							</div>
							<div className="mt-6 md:mt-8">
								<label className="text-sm md:text-base" htmlFor="email">Email</label>
								<input value={email} onChange={event => setEmail(event.target.value)} required className="h-8 w-full shadow-input appearance-none rounded mt-1.5 px-2 py-1.5 md:h-11" type="email" name="email" id="email" />
							</div>
							<div className="mt-6 md:mt-8">
								<label className="flex justify-between items-center text-sm md:text-base" htmlFor="phone">
									<p>Phone Number</p>
									<p className="text-gray-400">(optional)</p>
								</label>
								<input value={phone} onChange={event => setPhone(event.target.value)} className="h-8 w-full shadow-input appearance-none rounded mt-1.5 px-2 py-1.5 md:h-11" type="tel" name="phone" id="phone" />
							</div>
							<div className="mt-6 md:mt-8">
								<label className="text-sm md:text-base" htmlFor="comment">Comments</label>
								<textarea value={comments} onChange={event => setComments(event.target.value)} required className="h-24 w-full shadow-input appearance-none rounded mt-1.5 px-2 py-1.5 md:h-32 lg:h-40" id="comment" />
							</div>

							<TurnstileWidget
								ref={turnstileRef}
								onSuccess={(token: string) => setTurnstileToken(token)}
								onExpire={() => setTurnstileToken(null)}
								onError={() => setTurnstileToken(null)}
								onBeforeInteractive={() => setWidgetVisible(true)}
								className={widgetVisible ? 'mt-6 md:mt-8' : 'h-0'}
								options={{
									theme: 'light',
									appearance: 'interaction-only',
								}}
							/>

							<Submit type="submit" disabled={turnstileToken === null || status === Status.Loading} className="w-full mt-6 md:mt-8 lg:w-60 lg:mt-10">{status === Status.Loading ? 'Submitting' : 'Submit'}</Submit>
						</form>
					</div>
				</div>
			</div>
		</div>
	)
}

ContactUs.Layout = Layout
